import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import {
  DEFAULT_CLOSING_CHECKLIST,
  DEFAULT_OPENING_CHECKLIST,
} from '@/lib/checklist-defaults.js';
import { updateChecklistItems } from '@/lib/api/stores.js';
import type { ChecklistItemTemplate } from '@/lib/api/checklists.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

function newItemId() {
  return `custom_${Date.now().toString(36)}`;
}

function ChecklistEditorPanel({
  type,
  initialItems,
}: {
  type: 'open' | 'close';
  initialItems?: ChecklistItemTemplate[] | null;
}) {
  const queryClient = useQueryClient();
  const [items, setItems] = useState<ChecklistItemTemplate[]>([]);

  useEffect(() => {
    const seed = type === 'open' ? DEFAULT_OPENING_CHECKLIST : DEFAULT_CLOSING_CHECKLIST;
    const source =
      initialItems && initialItems.length > 0
        ? initialItems.map(({ id, label, automated, link }) => ({ id, label, automated, link }))
        : seed.map(({ id, label, automated, link }) => ({ id, label, automated, link }));
    setItems(source);
  }, [initialItems, type]);

  const saveMut = useMutation({
    mutationFn: () => updateChecklistItems({ type, items }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      toastSuccess(`${type === 'open' ? 'Opening' : 'Closing'} checklist saved`);
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function move(index: number, dir: -1 | 1) {
    const next = [...items];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    setItems(next);
  }

  return (
    <div className="space-y-3">
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={item.id} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-2">
            <div className="flex flex-col gap-0.5">
              <button
                type="button"
                disabled={index === 0}
                onClick={() => move(index, -1)}
                className="rounded p-0.5 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                disabled={index === items.length - 1}
                onClick={() => move(index, 1)}
                className="rounded p-0.5 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
              >
                <ArrowDown className="h-3.5 w-3.5" />
              </button>
            </div>
            <input
              className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
              value={item.label}
              onChange={(e) =>
                setItems((curr) =>
                  curr.map((row, i) => (i === index ? { ...row, label: e.target.value } : row)),
                )
              }
            />
            <input
              className="w-32 rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
              placeholder="Link (optional)"
              value={item.link ?? ''}
              onChange={(e) =>
                setItems((curr) =>
                  curr.map((row, i) =>
                    i === index ? { ...row, link: e.target.value || undefined } : row,
                  ),
                )
              }
            />
            <button
              type="button"
              onClick={() => setItems((curr) => curr.filter((_, i) => i !== index))}
              className="rounded-lg p-2 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() =>
            setItems((curr) => [...curr, { id: newItemId(), label: 'New checklist item' }])
          }
          className="inline-flex items-center gap-1 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50"
        >
          <Plus className="h-4 w-4" />
          Add item
        </button>
        <button
          type="button"
          disabled={saveMut.isPending || items.some((i) => !i.label.trim())}
          onClick={() => saveMut.mutate()}
          className="rounded-xl bg-primary px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          {saveMut.isPending ? 'Saving…' : `Save ${type === 'open' ? 'opening' : 'closing'} checklist`}
        </button>
      </div>
    </div>
  );
}

export function ChecklistItemsSettings({
  openingItems,
  closingItems,
  openingEnabled = false,
  closingEnabled = false,
  onToggleEnabled,
}: {
  openingItems?: ChecklistItemTemplate[] | null;
  closingItems?: ChecklistItemTemplate[] | null;
  openingEnabled?: boolean;
  closingEnabled?: boolean;
  onToggleEnabled?: (patch: {
    openingChecklistEnabled?: boolean;
    closingChecklistEnabled?: boolean;
  }) => void;
}) {
  const [tab, setTab] = useState<'open' | 'close'>('open');

  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
      <h3 className="text-lg font-semibold text-slate-950">Daily Checklists</h3>
      <p className="mt-1 text-sm text-slate-600">
        Off by default. Enable only the lists your store uses — opening and closing are independent.
        Historical completions remain viewable when a list is re-enabled.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800">
          <input
            type="checkbox"
            className="rounded border-slate-300"
            checked={openingEnabled}
            onChange={(e) => onToggleEnabled?.({ openingChecklistEnabled: e.target.checked })}
          />
          Opening checklist enabled
        </label>
        <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800">
          <input
            type="checkbox"
            className="rounded border-slate-300"
            checked={closingEnabled}
            onChange={(e) => onToggleEnabled?.({ closingChecklistEnabled: e.target.checked })}
          />
          Closing checklist enabled
        </label>
      </div>
      <div className="mt-4 flex gap-2 rounded-xl bg-slate-200/60 p-1">
        {(['open', 'close'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={[
              'flex-1 rounded-lg px-3 py-2 text-sm font-semibold',
              tab === t ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-600',
            ].join(' ')}
          >
            {t === 'open' ? 'Opening' : 'Closing'}
            {(t === 'open' ? !openingEnabled : !closingEnabled) ? ' (off)' : ''}
          </button>
        ))}
      </div>
      <div className="mt-4">
        {tab === 'open' ? (
          <ChecklistEditorPanel type="open" initialItems={openingItems} />
        ) : (
          <ChecklistEditorPanel type="close" initialItems={closingItems} />
        )}
      </div>
    </section>
  );
}
